// Service-sheet columns the app relies on but a sheet may not have yet:
//   • "Expected date" — a DATE column: each work's expected date (and the vessel's, on its own row);
//   • "Flag"          — a CHECKBOX column shown as Smartsheet's flag symbol: a vessel flagged for attention;
//   • "Technician"    — a plain text column: who carried out each work, typed in (it took the old
//                       Approval dropdown's place on the works; that column and its answers stay on the sheet);
//   • "Estimated Vessel Arrival Date" / "Actual Vessel Arrival Date" — DATE columns: when the vessel is
//                       expected at the yard, and when it really came (the first two of the vessel's five dates,
//                       before the Start, the Expected delivery and the Delivery date the sheet already has).
// The page finds them by title/type on the live sheet. When one is missing it still shows the field, and
// the first time someone sets it the worker adds the column to the Smartsheet (POST /api/service/ensure-column)
// so the value lives in the sheet like every other Service field.
export const SERVICE_EXTRA_COLUMNS = {
    expected: { title: 'Expected date', type: 'DATE' },
    flag: { title: 'Flag', type: 'CHECKBOX', symbol: 'FLAG' },
    technician: { title: 'Technician', type: 'TEXT_NUMBER' },
    expectedArrival: { title: 'Estimated Vessel Arrival Date', type: 'DATE' },
    arrival: { title: 'Actual Vessel Arrival Date', type: 'DATE' },
};
/** the stand-in column id the page uses until the real column exists (never a Smartsheet id: those are positive). */
export const SERVICE_PENDING_COLUMN_ID = { expected: -1, flag: -2, technician: -3, expectedArrival: -4, arrival: -5 };
/** the kind a stand-in column id stands for. */
export const serviceExtraKindOf = (id) => Object.keys(SERVICE_PENDING_COLUMN_ID).find((k) => SERVICE_PENDING_COLUMN_ID[k] === id);
// a plain "Expected" / "Expected date" (not "Expected arrival", which is something else), in English or Greek
const EXPECTED_TITLE = /^\s*(expected(\s+(date|completion|finish))?|εκτιμ[ωώ]μενη(\s+ημερομην[ιί]α)?|αναμεν[οό]μενη(\s+ημερομην[ιί]α)?)\s*$/i;
const FLAG_TITLE = /^\s*(flag(ged)?|σημα[ιί]α)\s*$/i;
// "Technician" / "Τεχνικός" (a text column: a dropdown of that name is something else)
const TECHNICIAN_TITLE = /^\s*(technician|τεχνικ[οό]ς(\s+εργασι[ωώ]ν)?)\s*$/i;
// the vessel's arrival: expected ("Estimated Vessel Arrival Date", "Expected arrival", "ETA", "Εκτιμώμενη
// άφιξη") and real ("Actual Vessel Arrival Date", "Arrival date", "Ημερομηνία άφιξης")
const EXPECTED_ARRIVAL_TITLE = /^\s*((estimated|expected|planned)\s+(vessel\s+|boat\s+)?arrival(\s+date)?|eta|(εκτιμ[ωώ]μενη|αναμεν[οό]μενη|προβλεπ[οό]μενη)\s+(ημερομην[ιί]α\s+)?[αά]φιξη[ςσ]?)\s*$/i;
const ARRIVAL_TITLE = /^\s*((actual\s+)?(vessel\s+|boat\s+)?arrival(\s+date)?|(πραγματικ[ηή]\s+)?(ημερομην[ιί]α\s+)?[αά]φιξη[ςσ]?)\s*$/i;
const isDate = (c) => c.type === 'DATE' || c.type === 'DATETIME';
/** the sheet's column for `kind`, if it has one. */
export function findServiceColumn(kind, cols) {
    if (kind === 'expected')
        return cols.find((c) => (c.type === 'DATE' || c.type === 'DATETIME') && EXPECTED_TITLE.test(c.title));
    if (kind === 'technician')
        return cols.find((c) => !c.primary && (c.type ?? 'TEXT_NUMBER') === 'TEXT_NUMBER' && TECHNICIAN_TITLE.test(c.title));
    if (kind === 'expectedArrival')
        return cols.find((c) => isDate(c) && EXPECTED_ARRIVAL_TITLE.test(c.title));
    if (kind === 'arrival')
        return cols.find((c) => isDate(c) && ARRIVAL_TITLE.test(c.title));
    return (cols.find((c) => c.type === 'CHECKBOX' && c.symbol === 'FLAG') ??
        cols.find((c) => c.type === 'CHECKBOX' && FLAG_TITLE.test(c.title)));
}
/** the Service sheet's Status column: its first colour-ball symbol column (RYG and the like), else a
 *  dropdown titled Status. Never a flag or star: Smartsheet draws those as symbols too, but they're
 *  checkboxes (the app's own Flag column was first added right after the name, ahead of Status, and once read as the
 *  Status column it blanked every status ball, colour and filter on the page). */
export function findServiceStatusColumn(cols) {
    const balls = cols.filter((c) => !!c.symbol && c.type !== 'CHECKBOX' && c.symbol !== 'FLAG' && c.symbol !== 'STAR');
    return balls[0] ?? cols.find((c) => /status|κατάσταση|state/i.test(c.title) && c.type === 'PICKLIST');
}
/** a column type as people call it, for the messages about adding one. */
export const SERVICE_COLUMN_TYPE_NAME = { DATE: 'date', CHECKBOX: 'checkbox', TEXT_NUMBER: 'text' };
/** what to do to have the `kind` column: use the one that's there, or add it (where), or say why not.
 *  Smartsheet column titles are unique, so a same-named column of another type blocks adding ours. */
export function planServiceColumn(kind, cols) {
    const found = findServiceColumn(kind, cols);
    if (found)
        return { existing: found.id };
    const spec = SERVICE_EXTRA_COLUMNS[kind];
    const clash = cols.find((c) => c.title.trim().toLowerCase() === spec.title.toLowerCase());
    if (clash) {
        return { conflict: `The Service sheet already has a “${clash.title}” column that isn't a ${SERVICE_COLUMN_TYPE_NAME[spec.type]} column. Rename it in Smartsheet, or change its type.` };
    }
    const idx = (c, i) => c.index ?? i;
    let index;
    const dates = cols.map((c, i) => ({ c, i })).filter(({ c }) => isDate(c));
    const at = (c) => (c ? idx(c, cols.indexOf(c)) : -1);
    if (kind === 'expected') {
        // beside the sheet's other dates (after the last one), else at the end
        index = dates.length ? Math.max(...dates.map(({ c, i }) => idx(c, i))) + 1 : cols.length;
    }
    else if (kind === 'expectedArrival' || kind === 'arrival') {
        // the vessel's arrival comes first among its dates: ahead of the first date column (the Start), the
        // estimated one ahead of the actual one, whichever is added first
        const first = dates.length ? Math.min(...dates.map(({ c, i }) => idx(c, i))) : cols.length;
        if (kind === 'expectedArrival') {
            const actual = at(findServiceColumn('arrival', cols));
            index = actual >= 0 ? actual : first;
        }
        else {
            const estimated = at(findServiceColumn('expectedArrival', cols));
            index = estimated >= 0 ? estimated + 1 : first;
        }
    }
    else {
        // a flag (or the technician) goes at the far right end of the sheet: added anywhere earlier it shifts the
        // sheet's own columns for everyone working in Smartsheet (and once sat ahead of Status, see
        // findServiceStatusColumn)
        index = cols.length ? Math.max(...cols.map((c, i) => idx(c, i))) + 1 : 0;
    }
    return { create: { ...spec, index } };
}
//# sourceMappingURL=serviceColumns.js.map